import { ReactElement, ReactNode, useId, useRef } from 'react';

import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { X } from 'lucide-react';

interface LegalDialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export default function LegalDialog({
  title,
  onClose,
  children,
}: LegalDialogProps): ReactElement {
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEntered: () => closeButtonRef.current?.focus() }}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          margin: { xs: '16px', sm: '32px' },
          width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          maxHeight: 'calc(100dvh - 64px)',
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          fontSize: '18px',
          fontWeight: 600,
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {title}
        <IconButton
          ref={closeButtonRef}
          onClick={onClose}
          aria-label={`Fechar ${title}`}
          title="Fechar"
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <X size={20} aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        tabIndex={0}
        sx={{
          padding: '20px',
          '&.MuiDialogContent-root': { paddingTop: '20px' },
          color: '#4b5563',
          overflowWrap: 'anywhere',
          '& p': { fontSize: '14px', lineHeight: 1.8, marginBottom: '16px' },
          '& h3': {
            fontSize: '14px',
            fontWeight: 600,
            color: '#111827',
            marginTop: '24px',
            marginBottom: '8px',
          },
          '& h3:first-child': { marginTop: 0 },
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}
